import {BehaviorSubject, combineLatest, firstValueFrom, Subject} from "rxjs";
import {Product} from "@app/core/product/domain/dtos/product";
import {map} from "rxjs/operators";
import {Injectable} from "@angular/core";
import {
    CreateShareProductsLinkUseCase
} from "@app/core/product/application/create-share-products-link-use-case.service";
import {PopupNotifier} from "@app/presentation/components/errors/popup.notifier";
import {MAX_SHAREABLE_PRODUCTS} from '@app/core/product/domain/max-sharable-products';

export class ShareableProduct {
    constructor(
        public readonly code: string,
        public readonly product: Product,
        public readonly selected: boolean,
    ) {}
}

@Injectable()
export class ShareProductsViewModel {

    readonly notifier = new PopupNotifier();

    private readonly selectedProductsSubject = new BehaviorSubject<Product[]>([]);
    readonly selectedProducts$ = this.selectedProductsSubject.asObservable();

    private readonly isProductsSharingLoadingSubject = new BehaviorSubject<boolean>(false);
    readonly isProductsSharingLoading$ = this.isProductsSharingLoadingSubject.asObservable();

    private readonly availableProductsSubject = new BehaviorSubject<Product[]>([]);

    readonly shareableProducts$ = combineLatest([
        this.selectedProducts$,
        this.availableProductsSubject.asObservable(),
    ]).pipe(
        map(([selectedProducts, availableProducts]) => {
            // Create a new ShareableProduct for each product, and mark them as selected if they are in the selectedProducts array
            return availableProducts.map(product => {
                const isSelected = selectedProducts.some(selectedProduct => selectedProduct.code === product.code);
                return new ShareableProduct(product.code, product, isSelected);
            })
        }),
    );

    readonly allProductsSelected$ = combineLatest([
        this.selectedProducts$,
        this.availableProductsSubject.asObservable(),
    ]).pipe(
        map(([selectedProducts, shareableProducts]) => selectedProducts.length === shareableProducts.length)
    );

    private sharedProductsUrlCreatedEventSubject = new Subject<string>();
    readonly sharedProductsUrlCreatedEvent$ = this.sharedProductsUrlCreatedEventSubject.asObservable();

    actionLoadProducts(products: Product[]): void {
        this.availableProductsSubject.next(products);
    }

    areSelectedProductsGreaterThanMax$ = this.selectedProducts$.pipe(
        map(selectedProducts => selectedProducts.length > MAX_SHAREABLE_PRODUCTS)
    );

    exceededSelectedProductsCount$ = this.selectedProducts$.pipe(
        map(selectedProducts => selectedProducts.length - MAX_SHAREABLE_PRODUCTS)
    );

    canShareProducts$ = combineLatest([
        this.shareableProducts$,
        this.selectedProducts$,
        this.areSelectedProductsGreaterThanMax$,
    ]).pipe(
        map(([shareableProducts, selectedProducts, areSelectedProductsGreaterThanMax]) => {
            const hasShareableProducts = shareableProducts.length > 0;
            const hasSelectedProducts = selectedProducts.length > 0;
            return hasShareableProducts && hasSelectedProducts && !areSelectedProductsGreaterThanMax;
        })
    );

    constructor(
        private createShareProductsUrlUseCase: CreateShareProductsLinkUseCase,
    ) {}

    actionSelectProduct(product: Product, selected: boolean) {
        if (selected) {
            this.selectedProductsSubject.next([...this.selectedProductsSubject.value, product]);
        } else {
            this.selectedProductsSubject.next(this.selectedProductsSubject.value.filter(p => p.code !== product.code));
        }
    }

    actionSelectAllProducts(selected: boolean) {
        if (selected) {
            this.selectedProductsSubject.next(this.availableProductsSubject.value.map(product => product));
        } else {
            this.selectedProductsSubject.next([]);
        }
    }

    async actionShareSelectedProducts() {
        try {
            this.isProductsSharingLoadingSubject.next(true);
            const selectedProducts = this.selectedProductsSubject.getValue();
            const selectedProductsCodes = selectedProducts.map(product => product.code);
            const url = await firstValueFrom(this.createShareProductsUrlUseCase.execute(selectedProductsCodes));
            this.sharedProductsUrlCreatedEventSubject.next(url);
        } catch (error: any) {
            this.notifier.notifyError(error);
        } finally {
            this.isProductsSharingLoadingSubject.next(false);
        }
    }

}